import { useState, useEffect } from "react";
import actions from "../config.json";
import passionMap from "../pages/OffersLandingPage/generatedPassionMap.json";
import { ENV } from "../env";

// Cache duration in milliseconds (24 hours)
const CACHE_DURATION = 24 * 60 * 60 * 1000;

/**
 * Categorize and limit properties based on passion map
 * @param {Array} properties - Array of property objects or IDs
 * @param {string} passion - The passion type to filter by
 * @returns {Object} Object with passionProperties and nonPassionProperties arrays
 */
const categorizeProperties = (properties, passion) => {
  const passionIds = passionMap[passion];
  const passionProperties = [];
  const nonPassionProperties = [];

  properties.forEach((property) => {
    const id = property.id || property;
    const propertyObj = typeof property === "object" ? property : { id };
    const isPassion = passionIds.includes(id);
    const enrichedProperty = { ...propertyObj, isPassion };

    if (isPassion) {
      passionProperties.push(enrichedProperty);
    } else {
      nonPassionProperties.push(enrichedProperty);
    }
  });

  return { passionProperties, nonPassionProperties };
};

/**
 * Get limited properties prioritizing passion properties
 * @param {Array} passionProperties - Passion properties
 * @param {Array} nonPassionProperties - Non-passion properties
 * @returns {Array} Limited array of properties
 */
const getLimitedProperties = (
  passionProperties,
  nonPassionProperties,
  maxOverride = 250,
) => {
  if (passionProperties.length >= maxOverride) {
    return passionProperties.slice(0, maxOverride);
  }

  const remainingCount = maxOverride - passionProperties.length;
  return [
    ...passionProperties,
    ...nonPassionProperties.slice(0, remainingCount),
  ];
};

/**
 * Hook to fetch basic offer data (passion info and property IDs)
 * @param {string} offerId - The offer ID to fetch data for
 * @param {string} passion - The passion type
 * @returns {Object} - Basic offer data and loading state
 */
export const useOfferBasicData = (offerId, passion) => {
  const [offerBasicData, setOfferBasicData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check if cached data is valid
   * @param {Object} cachedData - The cached data to check
   * @returns {boolean} - Whether the cache is valid
   */
  const isCacheValid = (cachedData) => {
    if (!cachedData?.timestamp || !cachedData?.hotels?.length) {
      return false;
    }

    return Date.now() - cachedData.timestamp < CACHE_DURATION;
  };

  /**
   * Fetch the basic offer data from the server or cache
   */
  const fetchOfferBasicData = async () => {
    if (!offerId) {
      setError("Offer ID is required");
      return;
    }

    setLoading(true);
    setError(null);

    const cacheKey = `marriott-offer-basic-${offerId}`;
    try {
      const cachedDataString = localStorage.getItem(cacheKey);
      if (ENV.FEATURE_FLAGS.offersCaching && cachedDataString) {
        const cachedData = JSON.parse(cachedDataString);

        if (isCacheValid(cachedData)) {
          console.log(`Using cached basic data for offer ${offerId}`);
          setOfferBasicData(cachedData);
          setLoading(false);
          return;
        } else {
          console.log(
            `Cached basic data for offer ${offerId} is expired, fetching fresh data`,
          );
        }
      }

      const actionUrl = ENV.FEATURE_FLAGS.useMbopQueries
        ? "marriott-passions-v3/get-offer-data-mbop"
        : "marriott-passions-v3/get-offer-data";
      const hotelsUrl = actions[actionUrl];

      const response = await fetch(`${hotelsUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          offerId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error fetching basic offer data: ${response.status}`);
      }

      const responseData = await response.json();
      const { passionProperties, nonPassionProperties } = categorizeProperties(
        responseData?.hotels || [],
        passion,
      );

      const propertiesToFetch = getLimitedProperties(
        passionProperties,
        nonPassionProperties,
        250,
      );

      const basicData = {
        ...responseData,
        propertiesToFetch,
        timestamp: Date.now(),
      };

      // Cache the basic data
      if (ENV.FEATURE_FLAGS.offersCaching) {
        localStorage.setItem(cacheKey, JSON.stringify(basicData));
      }

      setOfferBasicData(basicData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching basic offer data:", err);
      setError(err.message || "Failed to fetch basic offer data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOfferBasicData();
  }, [offerId, passion]);

  return {
    offerBasicData,
    loading,
    error,
    fetchOfferBasicData,
  };
};
